import React from 'react';
import {
  Button,
  Flex,
  Heading,
  Icon,
  icons,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  List,
  ListIcon,
  ListItem,
  Link,
  Text,
  Box,
} from '@tapresearch/ui/components';
import { useEnablePlayerInsights } from '../../organization_dashboard/api/PlayerInsightsApi';

const StyledListItem = ({ text }: { text: string }) => (
  <ListItem {...Styles.ActivateModal.ListItem}>
    <ListIcon as={icons.fi.ChevronRight} boxSize={6} color="blue.500" />
    {text}
  </ListItem>
);

export const ActivatePlayerInsightsModal = ({
  isOpen,
  onClose,
  isAdmin,
  redirectLink,
  orgIdentifier,
}: {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  redirectLink: string;
  orgIdentifier: string;
}) => {
  const { mutateAsync: enablePlayerInsights, isLoading } =
    useEnablePlayerInsights();

  const onActivatePlayerInsights = async () => {
    await enablePlayerInsights(orgIdentifier);
    window.location.href = redirectLink;
  };

  return (
    <Modal
      isCentered
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent p={8} rounded="2xl" w="90vw" maxWidth="900px">
        <ModalCloseButton />
        <ModalBody>
          <Heading {...Styles.ActivateModal.Header}>
            <Icon boxSize="50px" mr={3} icon={icons.md.PersonSearch} />
            Player Insights
          </Heading>
          <Flex direction="column">
            <Text {...Styles.ActivateModal.DescriptionText}>
              TapResearch makes it fast and easy to access real-time player
              feedback so teams can make better decisions, <i>faster</i>.
            </Text>
            <Text {...Styles.ActivateModal.DescriptionText} mt={4}>
              Built for <b>Product</b>, <b>Marketing</b>, <b>LiveOps teams</b>.
            </Text>
            <Text {...Styles.ActivateModal.DescriptionText} mt={6}>
              Here are some of the ways you can use Player Insights:
            </Text>
            <List ml={1}>
              <StyledListItem text="Inform product roadmaps with real-time player feedback" />
              <StyledListItem text="Engage users outside of support channels" />
              <StyledListItem text="Equip liveOps with player preference data for tailored experiences" />
            </List>
          </Flex>
        </ModalBody>
        <ModalFooter
          flexDirection="column"
          borderTop="none"
          justifyContent="center"
        >
          <Flex {...Styles.ActivateModal.ReadyText}>Ready to get started?</Flex>
          {isAdmin ? (
            <Button
              isLoading={isLoading}
              size="lg"
              h={14}
              rounded="md"
              colorScheme="forest_green"
              minW="400px"
              onClick={() => onActivatePlayerInsights()}
            >
              Activate Now
            </Button>
          ) : (
            <Text fontStyle="italic" fontSize="md">
              Contact your organization administrator to activate.
            </Text>
          )}

          <Box mt={12} mb={4} fontWeight="medium" fontSize="md">
            Questions?{' '}
            <Link
              href="mailto: developers@tapresearch.com"
              ml={1}
              color="blue.500"
            >
              Get in touch.
            </Link>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Styles = {
  ActivateModal: {
    Header: {
      letterSpacing: 'tight',
      fontWeight: 'light',
      color: 'blue.500',
      mb: 8,
    },
    DescriptionText: {
      fontSize: 'lg',
      fontWeight: 'normal',
      maxW: '700px',
      mb: 4,
      lineHeight: 'tall',
    },
    ListItem: {
      fontSize: 'md',
      fontWeight: 'medium',
      p: 1,
      color: 'gray.700',
    },
    ReadyText: {
      w: 'full',
      justifyContent: 'center',
      borderTop: '1px solid',
      borderColor: 'gray.200',
      mt: 4,
      pt: 6,
      mb: 4,
      fontSize: '2xl',
      color: 'gray.600',
      fontWeight: 'medium',
    },
  },
} as const;
